import React, { useState, useEffect } from 'react';
import useRepo from '../../hooks/useRepo';
import useApiContext from 'hooks/useApiContext';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { T, M, A } from 'TOMA';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { InputText, InputSelect } from '../../form';
import s from '../Event/Event.module.scss';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import routes from 'router/routes';

function CategoryFormPage(): JSX.Element {
  const { categoryRepository } = useApiContext();
  const { categoryId } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [loadingForm, setLoadingForm] = useState(false);
  const [successForm, setSuccessForm] = useState(false);
  const [errorForm, setErrorForm] = useState<string>();
  const [categories, setCategories] = useState<any[]>([]);
  const { t } = useTranslation();
  const { response, loading } = useRepo(async () => {
    if (categoryId && categoryId !== 'new-category') {
      return await categoryRepository.getCategoryById(categoryId);
    }
    return null;
  }, [categoryId, pathname]);

  const { response: parentCategories } = useRepo(async () => {
    return await categoryRepository.parentCategory('universe');
  }, [pathname]);

  useEffect(() => {
    if (parentCategories) {
      setCategories(parentCategories);
    }
  }, [parentCategories]);

  const schema = yup.object({
    topicCode: yup.string().max(50).required(t('common:form.error.required')),
    code: yup.string().max(50).required(t('common:form.error.required')),
    label: yup.string().max(255).required(t('common:form.error.required')),
    parent_id: yup.mixed().required(t('common:form.error.required')),
  });
  const form = useForm<any>({
    mode: 'onChange',
    reValidateMode: 'onBlur',
    resolver: yupResolver(schema),
    defaultValues: {
      topic: '',
      topicCode: '',
      code: '',
      label: '',
      filter: '',
      label_en: '',
      parent_id: null,
    },
  });

  const { handleSubmit, control, reset, getValues } = form;

  useEffect(() => {
    if (categoryId && !loading && response) {
      reset({
        topic: response.topic,
        topicCode: response.topicCode,
        code: response.code,
        label: response.label,
        filter: response.filter,
        label_en: response.label_en,
        parent_id: {
          value: response.parent_id ?? '',
          label:
            categories.find((cat) => cat.id === response.parent_id)?.label ??
            '',
        },
      });
    }
  }, [loading, response, reset, categoryId, parentCategories, categories]);

  function onSubmit(formData: any): void {
    setLoadingForm(true);
    setErrorForm('');
    setSuccessForm(false);

    // Transform the form data for API submission
    const apiData = {
      topic: 'category',
      topicCode: formData.topicCode,
      code: formData.code,
      label: formData.label,
      filter: formData.filter,
      label_en: formData.label_en,
      parent_id: formData.parent_id?.value || formData.parent_id || null,
    };

    if (categoryId && categoryId !== 'new-category') {
      categoryRepository
        .updateCategory(categoryId, apiData)
        .then(() => {
          toast.success(t('categories:update_success'));
          setSuccessForm(true);
          setTimeout(() => {
            navigate(routes.categories.path);
          }, 1500);
        })
        .catch((err: Error) => {
          setErrorForm(err.message);
          toast.error(err.message || t('categories:update_error'));
        })
        .finally(() => {
          setLoadingForm(false);
        });
    } else {
      categoryRepository
        .createCategory(apiData)
        .then(() => {
          toast.success(t('categories:create_success'));
          setSuccessForm(true);
          setTimeout(() => {
            navigate(routes.categories.path);
          }, 1500);
        })
        .catch((err: Error) => {
          console.log('Error creating category:', err);
          setErrorForm(err.message);
          toast.error(err.message || t('categories:create_error'));
        })
        .finally(() => {
          setLoadingForm(false);
        });
    }
  }

  return (
    <M.Loading loading={loading} error={null} fullWiewport>
      <T.Container py="50">
        <T.Block pb="50" mw="medium" m="auto" p="20" borderRadius="7">
          <A.Title as="h1" fontSize="48" mb="30" fw="black" textAlign="center">
            {categoryId && categoryId !== 'new-category'
              ? t('categories:edit_category')
              : t('categories:create_a_new_category')}
          </A.Title>

          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <T.Block mb="20">
              <InputText
                label={t('categories:topicCode')}
                name="topicCode"
                type="text"
                control={control}
                disabled={false}
                required
              />
            </T.Block>
            <T.Block mb="20">
              <InputText
                label={t('categories:code')}
                name="code"
                type="text"
                control={control}
                disabled={false}
                required
              />
            </T.Block>
            <T.Block mb="20">
              <InputText
                label={t('categories:label')}
                name="label"
                type="text"
                control={control}
                disabled={false}
                required
              />
            </T.Block>
            <T.Block mb="20">
              <InputText
                label={t('categories:filter')}
                name="filter"
                type="text"
                control={control}
                disabled={false}
                required
              />
            </T.Block>
            <T.Block mb="20">
              <InputText
                label={t('categories:label_english')}
                name="label_en"
                type="text"
                control={control}
                disabled={false}
              />
            </T.Block>
            <T.Block mb="20">
              <Controller
                name="parent_id"
                control={control}
                render={({ field }) => (
                  <A.InputAutocomplete
                    {...field}
                    label={t('categories:parent_category')}
                    name="parent_id"
                    options={[
                      ...categories?.map((item) => {
                        return {
                          value: item.id,
                          label: item.label,
                        };
                      }),
                    ]}
                  />
                )}
              />
            </T.Block>
            <T.Block mt="30" mb="20">
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: '10px',
                }}
              >
                <A.Button
                  type="submit"
                  bg="success"
                  loading={loadingForm}
                  disabled={loadingForm}
                >
                  {categoryId && categoryId !== 'new-category'
                    ? t('common:update')
                    : t('common:create')}
                </A.Button>
                {/* <A.Button
                  type="button"
                  appearance="link"
                  color="grey-80"
                  textDecoration="underline"
                  onClick={() => navigate(routes.categories.path)}
                >
                  {t('common:cancel')}
                </A.Button> */}
              </div>
            </T.Block>

            <T.Block mt="20">
              <A.Alert alert="success" side="🎉" mb="15">
                {successForm &&
                  (categoryId && categoryId !== 'new-universe'
                    ? t('categories:update_success')
                    : t('categories:create_success'))}
              </A.Alert>
              <A.Alert alert="error" side="⚠️" mb="15">
                {errorForm && <A.InnerHTML html={errorForm} />}
              </A.Alert>
            </T.Block>
          </form>
        </T.Block>
      </T.Container>
    </M.Loading>
  );
}

export default CategoryFormPage;
